import React, { useEffect, useState } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { useAppContext } from './context/AppContext';
import { AnimatePresence, motion } from 'framer-motion';

// Pages
import HomePage from './pages/HomePage';
import TournamentSetupPage from './pages/TournamentSetupPage';
import TeamManagementPage from './pages/TeamManagementPage';
import MatchPlayPage from './pages/MatchPlayPage';
import SchedulePage from './pages/SchedulePage';
import LeaderboardPage from './pages/LeaderboardPage';
import AnalyticsPage from './pages/AnalyticsPage';
import SettingsPage from './pages/SettingsPage';
import PastMatchesPage from './pages/PastMatchesPage';

// Components
import TabBar from './components/ui/TabBar';
import Header from './components/ui/Header';
import PWAInstallPrompt from './components/ui/PWAInstallPrompt';

function App() {
  const { darkMode } = useAppContext();
  const location = useLocation();
  const [showPWAPrompt, setShowPWAPrompt] = useState(false);

  // Determine if we should show the TabBar
  const showTabBar = location.pathname !== '/';

  // Apply dark mode class to body
  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [darkMode]);

  // Handle PWA install prompt
  useEffect(() => {
    // Don't show install prompt if already installed
    const isAppInstalled = () => {
      // iOS detection
      if (window.navigator.standalone) {
        return true;
      }

      // Android/Desktop detection
      if (window.matchMedia('(display-mode: standalone)').matches) {
        return true;
      }

      return false;
    };

    if (isAppInstalled()) {
      return;
    }

    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      
      // Store the event for later use
      window.deferredPrompt = e;

      // Show the install prompt after a delay
      setTimeout(() => {
        if (!isAppInstalled()) {
          setShowPWAPrompt(true);
        }
      }, 3000);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstall = async () => {
    // Get the deferred prompt from window object
    const deferredPrompt = window.deferredPrompt;
    if (!deferredPrompt) return;

    // Show the install prompt
    deferredPrompt.prompt();
    
    // Wait for the user to respond to the prompt
    const { outcome } = await deferredPrompt.userChoice;
    console.log(`Installation outcome: ${outcome}`);

    // Clear the prompt
    window.deferredPrompt = null;
    setShowPWAPrompt(false);
  };

  const dismissPrompt = () => {
    setShowPWAPrompt(false);
  };

  return (
    <div className="flex flex-col min-h-screen bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark">
      {showTabBar && <Header />}

      <main className="flex-1 overflow-auto pb-20">
        <AnimatePresence mode="wait">
          <motion.div
            key={location.pathname}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2 }}
            className="h-full"
          >
            <Routes location={location}>
          <Route path="/" element={<HomePage />} />
          <Route path="/setup" element={<TournamentSetupPage />} />
          <Route path="/teams" element={<TeamManagementPage />} />
          <Route path="/match/:id" element={<MatchPlayPage />} />
          <Route path="/schedule" element={<SchedulePage />} />
          <Route path="/leaderboard" element={<LeaderboardPage />} />
          <Route path="/analytics" element={<AnalyticsPage />} />
          <Route path="/past-matches" element={<PastMatchesPage />} />
          <Route path="/settings" element={<SettingsPage />} />
        </Routes>
          </motion.div>
        </AnimatePresence>
      </main>

      {showTabBar && <TabBar />}

      {/* PWA install prompt */}
      {showPWAPrompt && (
        <PWAInstallPrompt
          onInstall={handleInstall}
          onDismiss={dismissPrompt}
        />
      )}
    </div>
  );
}

export default App;
